import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StoreContext } from '../../../models/import';
import Tooltip, { TooltipAlignEnum, TooltipCursorEnum, TooltipModifier } from '../../tooltip';
import { TextField, MenuItem, Select, Checkbox, InputLabel, FormControl } from '@mui/material';
import FormCheckbox, { FormCheckboxModifier } from 'components/form/checkbox';

export interface ValidationDepositWithdrawalTableFilterValues {
  currencies: number;
  exchanges: string[];
  match_same_exchange: boolean;
  amount_accuracy: number;
  time_accuracy: number;
  already_matched: boolean;
}
export interface ValidationDepositWithdrawalTableFiltersProps { 
  filters: ValidationDepositWithdrawalTableFilterValues;
  onFitlerCB: (e: ValidationDepositWithdrawalTableFilterValues) => void;

  disabled: boolean;
  tooltipAlignment: TooltipAlignEnum;
  isHidden: boolean;
}

export default function ValidationDepositWithdrawalTableFilters(props: ValidationDepositWithdrawalTableFiltersProps) {
  const {
    filters,
    onFitlerCB,
    disabled,
    tooltipAlignment,
    isHidden,
  } = props;
  const { t } = useTranslation();

  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const prevFilterRef = useRef<string | null>(null);
  const { userSettings } = useContext(StoreContext);

  const currencyTypeItems = [
    { value: 0,//ALL
      text: t('With user currency', { ns: 'validation' }),
    }, {
      value: 1,//EXCEPT PERSONAL
      text: t('Without user currency', { ns: 'validation' }),
    }];
    //There is also value: 2 for 'Only coins' which we don't use at the moment

    const accuracyTypeItemStrict = {
      value: true,
      amount_accuracy: 100,
      time_accuracy: 1,
      text: t('Strict', { ns: 'validation' }),
    };
    const accuracyTypeItemModerate = { 
      value: false,
      amount_accuracy: 90, 
      time_accuracy: 3,
      text: t('Moderate', { ns: 'validation' }),
    };

    const mapCurrencyTypeOutput = (currencies: boolean | undefined): number => {
      return (currencies !== undefined && currencies === true) ? 0 : 1
    };
    const mapCurrencyTypeInput = (currencies: number): boolean => {
      return currencies === 0 ? true : false;
    };

    const mapAccuracyAmountOutput = (value: boolean | undefined): number => {
      return (value !== undefined && value === true) ? accuracyTypeItemStrict.amount_accuracy : accuracyTypeItemModerate.amount_accuracy
    };
    const mapAccuracyTypeOutput = (value: boolean | undefined): number => {
      return (value !== undefined && value === true) ? accuracyTypeItemStrict.time_accuracy : accuracyTypeItemModerate.time_accuracy
    };
    const mapAccuracyInput = (amount: number | undefined, time: number | undefined): boolean => {
      if (amount === accuracyTypeItemStrict.amount_accuracy && time === accuracyTypeItemStrict.time_accuracy) {
        return accuracyTypeItemStrict.value;
      }
      if (amount === accuracyTypeItemModerate.amount_accuracy && time === accuracyTypeItemModerate.time_accuracy) {
        return accuracyTypeItemModerate.value;
      }
      return false;
    };

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      ...filters,
      currencies: mapCurrencyTypeInput(filters.currencies),
      accuracy: mapAccuracyInput(filters.amount_accuracy, filters.time_accuracy),
    },
  });

  useEffect(() => {
    const subscription = formMethods.watch((data) => {
      const { currencies } = data;
      const mappedData = {
        ...data,
        currencies: mapCurrencyTypeOutput(currencies),
        time_accuracy: mapAccuracyTypeOutput(data.accuracy),
        amount_accuracy: mapAccuracyAmountOutput(data.accuracy),
      };
      const jsonString = JSON.stringify(mappedData);
      if (prevFilterRef.current && prevFilterRef.current === jsonString) return;

      prevFilterRef.current = jsonString;
      onFitlerCB(mappedData);
    });

    if (isFirstLoad) {
      setIsFirstLoad(false);
      formMethods.setValue('currencies', filters.currencies, { shouldValidate: true });
    }

    return () => {
      subscription.unsubscribe();
    };
  }, [formMethods.watch]);

  return (
    <FormProvider {...formMethods}>
      {!isHidden && (
      <form className={`ct_form ${disabled ? '_inactive' : '_active'}`} onSubmit={(e) => e.preventDefault()}>
        <div className="ct_tableFilter">

          <div className="ct_tableFilter-ignoreUserCurrencyMismatches">
            <Controller
              control={formMethods.control}
              name="accuracy"
              render={({ field: { name, value, onChange }, formState }) => (
                <FormCheckbox
                  name={name}
                  label={t('Strict Accuracy', { ns: 'validation' })}
                  defaultChecked={value}
                  modifiers={[FormCheckboxModifier.OLD]}
                  onChange={onChange}
                  tooltip={{
                    align: tooltipAlignment,
                    message: t('Show matches with strict or moderate accuracy', { ns: 'validation' }),
                  }}
                />
              )}
            />
          </div>

          <div className="ct_tableFilter-ignoreUserCurrencyMismatches">
            <Controller
              control={formMethods.control}
              name="currencies"
              render={({ field: { name, value, onChange }, formState }) => (
                <FormCheckbox
                  name={name}
                  label={t('Currency Type', { ns: 'validation' })}
                  defaultChecked={value}
                  modifiers={[FormCheckboxModifier.OLD]}
                  onChange={onChange}
                  tooltip={{
                    align: tooltipAlignment,
                    message: t('Show results with or without user currency', { ns: 'validation' }),
                  }}
                />
              )}
            />
          </div>

        </div>
      </form>
      )}
    </FormProvider>
  );
}
